import React, { useEffect, useState, forwardRef, useMemo } from 'react';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { Card, Col, Row, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import IconItem from 'components/common/icon/IconItem';
import ImagenPerfil from 'components/common/ImagenPerfil';

import { useToast } from 'context/providers/ToastProvider';

const formatDateLabel = (startDate, endDate) => {
    const today = new Date().toLocaleDateString('es-MX', {
        year: 'numeric', month: 'short', day: '2-digit', hour12: false
    });
    
    const start = new Date(startDate).toLocaleDateString('es-MX', {
        year: 'numeric', month: 'short', day: '2-digit', hour12: false
    });
    
    const end = new Date(endDate).toLocaleDateString('es-MX', {
        year: 'numeric', month: 'short', day: '2-digit', hour12: false
    });

    if (start === today) return 'Hoy';
    if (start === end) return start;
    return `${start} - ${end}`;
};

const GreetingsDateInput = forwardRef(({ value, onClick }, ref) => (
    <div className="position-relative">
        <Form.Control
            size="sm"
            ref={ref}
            onClick={onClick}
            value={value}
            className="ps-4"
            readOnly
        />
        <FontAwesomeIcon
            icon="calendar-alt"
            className="text-primary position-absolute top-50 translate-middle-y ms-2"
        />
    </div>
));

GreetingsDateInput.propTypes = {
    value: PropTypes.string,
    onClick: PropTypes.func
};

const StatsItem = ({ stat }) => {
    const {
        icon,
        color,
        title,
        amount,
        suffix,
        badgeText
    } = stat;

    const numberColor = amount > 0 ? 'success' : 'danger';

    return (
        <>
            <Flex
                justifyContent="center"
                alignItems="center"
                className="mb-2 position-static"
            >
                <IconItem
                    tag="div"
                    icon={icon}
                    bg={`soft-${color}`}
                    color={color}
                    size="sm"
                    iconClass="fs--1"
                    className="me-2 shadow-none"
                />
                <h6 className="mb-0 flex-1 fs--1">{title}</h6>
            </Flex>
            <Flex
                justifyContent="center"
                alignItems="center"
                className="mb-1"
            >
                <p className={`font-sans-serif lh-1 mb-0 fs-2 text-${numberColor} fw-bold`}>
                    {amount}{suffix && <span className="fs--1 ms-1 text-muted">{suffix}</span>}
                </p>
            </Flex>
            {badgeText && (
                <Flex justifyContent="center">
                    <small className="text-muted text-center fs--2">{badgeText}</small>
                </Flex>
            )}
        </>
    );
};

StatsItem.propTypes = {
    stat: PropTypes.shape({
        icon: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        suffix: PropTypes.string,
        badgeText: PropTypes.string
    })
};

const DailyStatistics = () => {
const { token, setToken, idUsuario } = useToken();
const { showErrorToast } = useToast();
const [cargando, setCargando] = useState(true);
const [dailyStatistics, setdailyStatistics] = useState({});
const [vendedorInfo, setVendedorInfo] = useState(null);

const [vendedores, setVendedores] = useState([]);
const [vendedor, setVendedor] = useState(idUsuario || '');
const [cargandoVendedores, setCargandoVendedores] = useState(false);

const date = new Date();
    const [startDate, setStartDate] = useState(date);
    const [endDate, setEndDate] = useState(new Date());
    const [startDateStt, setstartDateStt] = useState(new Date());
    const [endDateStt, setEndDateStt] = useState(new Date());

    useEffect(() => {
        fetchVendedores();
    }, []);

    useEffect(() => {
        if (idUsuario && vendedor === '') {
            setVendedor(idUsuario);
        }
    }, [idUsuario]);

    useEffect(() => {
        getDailyStatistics(startDate, endDate);
    }, [vendedor]);

    const fetchVendedores = async () => {
        setCargandoVendedores(true);
        try {
            const response = await fetch('/api/leads/getVendedores', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            if (response?.ok) {
                const data = await response.json();
                setVendedores(data);
            } else {
                showErrorToast(`${response?.status} ${response?.statusText}`);
                if (response?.status === 401) {
                    setToken(null);
                    location.href = '';
                }
            }
        } catch (error) {
            showErrorToast('Error al cargar vendedores: ' + error.message);
        } finally {
            setCargandoVendedores(false);
        }
    };

    const getDailyStatistics = async (start, end) => {
        setCargando(true);

        const _startDate_ = new Date(start).toLocaleDateString('es-MX', {
            year: 'numeric', month: '2-digit', day: '2-digit', hour12: false
        });

        const _endDate_ = new Date(end).toLocaleDateString('es-MX', {
            year: 'numeric', month: '2-digit', day: '2-digit', hour12: false
        });

        const jDataSend = {
            startDate: _startDate_,
            endDate: _endDate_,
            vendedor
        };

        try {
            const response = await fetch("/api/leads/getDailyStatistics/", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(jDataSend)
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {
                    const ObjDetalles = JSON.parse(data.detalles);

                    const DailyStatisticsVar = {
                        Seguimiento: ObjDetalles.table2[0]?.TotalFollowingLeads || 0,
                        SeguimientoPromedio: ObjDetalles.table2[0]?.AVGFollowingLeads || 0,
                        ContactoInicial: 0,
                        Prospecto: 0,
                        Potencial: 0,
                        ClienteGanado: 0,
                        NoInteresado: 0,
                        RecompraEnProceso: 0,
                        RecompraSatisfactoria: 0,
                        OrdenesNuevas: 0,
                        OrdenesRecompra: 0
                    };

                    ObjDetalles.table3?.forEach((item) => {
                        const etapaMap = {
                            1: 'ContactoInicial',
                            2: 'Prospecto',
                            3: 'Potencial',
                            4: 'ClienteGanado',
                            5: 'NoInteresado',
                            6: 'RecompraEnProceso',
                            7: 'RecompraSatisfactoria'
                        };
                        
                        if (etapaMap[item.idEtapa]) {
                            DailyStatisticsVar[etapaMap[item.idEtapa]] = item.RegisteredLeads;
                        }
                    });

                    ObjDetalles.table4?.forEach((item) => {
                        if (item.bIsRecompra === 0) {
                            DailyStatisticsVar.OrdenesNuevas = item.numOrdenes;
                        } else if (item.bIsRecompra === 1) {
                            DailyStatisticsVar.OrdenesRecompra = item.numOrdenes;
                        }
                    });

                    if (ObjDetalles.table5 && ObjDetalles.table5.length > 0) {
                        setVendedorInfo({
                            nombre: ObjDetalles.table5[0].vNombreCompleto || 'Vendedor',
                            usuario: ObjDetalles.table5[0].vUsuario || '',
                            idUsuario: parseInt(vendedor)
                        });
                    } else
                        setVendedorInfo(null);

                    setstartDateStt(start);
                    setEndDateStt(end);
                    setdailyStatistics(DailyStatisticsVar);
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast(`${response.status} ${response.statusText}`);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición: ' + error);
            console.error('Error en getDailyStatistics:', error);
        } finally {
            setCargando(false);
        }
    };

    const onChange = dates => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);

        if (start && end) {
            getDailyStatistics(start, end);
        }
    };

    const dateLabel = useMemo(() => 
        formatDateLabel(startDateStt, endDateStt), 
        [startDateStt, endDateStt]
    );

    const statsConfig = useMemo(() => {
        const avg = dailyStatistics.SeguimientoPromedio || 0;
        const seguimientoColor = avg < 25 ? 'danger' : avg < 49 ? 'warning' : 'success';

        return [
            {
                icon: 'tasks',
                color: seguimientoColor,
                title: 'Seguimiento Leads',
                amount: dailyStatistics.Seguimiento || 0,
                suffix: `/ AVG ${avg}`,
                badgeText: dateLabel
            },
            {
                icon: 'user-plus',
                color: 'secondary',
                title: 'Contacto Inicial',
                amount: dailyStatistics.ContactoInicial || 0,
                badgeText: dateLabel
            },
            {
                icon: 'user-friends',
                color: 'primary',
                title: 'Prospectos',
                amount: dailyStatistics.Prospecto || 0,
                badgeText: dateLabel
            },
            {
                icon: 'star',
                color: 'info',
                title: 'Potencial',
                amount: dailyStatistics.Potencial || 0,
                badgeText: dateLabel
            },
            {
                icon: 'trophy',
                color: 'success',
                title: 'Clientes Ganados',
                amount: dailyStatistics.ClienteGanado || 0,
                suffix: `vs ${dailyStatistics.OrdenesNuevas || 0} CO`,
                badgeText: dateLabel
            },
            {
                icon: 'times-circle',
                color: 'danger',
                title: 'No Interesado',
                amount: dailyStatistics.NoInteresado || 0,
                badgeText: dateLabel
            },
            {
                icon: 'sync',
                color: 'warning',
                title: 'Recompra en Proceso',
                amount: dailyStatistics.RecompraEnProceso || 0,
                badgeText: dateLabel
            },
            {
                icon: 'check-circle',
                color: 'primary',
                title: 'Recompra Satisfactoria',
                amount: dailyStatistics.RecompraSatisfactoria || 0,
                suffix: `vs ${dailyStatistics.OrdenesRecompra || 0} CO`,
                badgeText: dateLabel
            }
        ];
    }, [dailyStatistics, dateLabel]);

    const LoadingSkeleton = () => (
        <Card>
            <Card.Body>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <DatePicker
                        selected={startDate}
                        onChange={onChange}
                        startDate={startDate}
                        formatWeekDay={day => day.slice(0, 3)}
                        endDate={endDate}
                        selectsRange
                        dateFormat="MMM dd"
                        customInput={<GreetingsDateInput />}
                    />
                </div>
                <Row>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Col xxl={3} xl={3} lg={4} md={4} sm={6} xs={12} key={i} className="mb-3">
                            <div className="p-2">
                                <Skeleton width={90} height={14} className="mb-2" />
                                <Skeleton width={120} height={28} className="mb-2" />
                                <Skeleton width={100} height={12} />
                            </div>
                        </Col>
                    ))}
                </Row>
            </Card.Body>
        </Card>
    );

    if (cargando) {
        return <LoadingSkeleton />;
    }

    return (
        <Card className="mb-2 p-2">
            <Card.Body>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div className="d-flex align-items-center gap-2">
                        <h6 className="mb-0 text-primary fw-bold">
                            <FontAwesomeIcon icon="chart-line" className="me-2" />
                            Estadísticas
                        </h6>
                        {vendedorInfo && (
                            <div className="d-flex align-items-center ms-3 ps-3" style={{ borderLeft: '2px solid #e0e0e0' }}>
                                <div className="me-2">
                                    <ImagenPerfil idUsuario={vendedorInfo.idUsuario} />
                                </div>
                                <div>
                                    <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                                        {vendedorInfo.nombre}
                                    </div>
                                    {vendedorInfo.usuario && (
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            @{vendedorInfo.usuario}
                                        </small>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Form.Select
                            size="sm"
                            value={vendedor}
                            disabled={cargandoVendedores}
                            onChange={(e) => setVendedor(e.target.value)}
                            style={{ minWidth: '160px' }}
                        >
                            <option value="">Todos</option>
                            {vendedores.map((v, i) => (
                                <option key={i} value={v.idUsuario}>
                                    {`${v.vNombre} ${v.vApellidos}`.trim()}
                                </option>
                            ))}
                        </Form.Select>
                        <small className="text-muted d-none d-md-inline">Período:</small>
                        <DatePicker
                            selected={startDate}
                            onChange={onChange}
                            startDate={startDate}
                            formatWeekDay={day => day.slice(0, 3)}
                            endDate={endDate}
                            selectsRange
                            dateFormat="MMM dd"
                            customInput={<GreetingsDateInput />}
                        />
                    </div>
                </div>

                <Row>
                <hr></hr>
                    {statsConfig.length === 0 ? (
                        <Col xs={12} className="text-center py-3">
                            <p className="text-muted">No hay datos disponibles</p>
                        </Col>
                    ) : (
                        statsConfig.map((stat, index) => (
                            <Col
                                xxl={3}
                                xl={3}
                                lg={4}
                                md={4}
                                sm={6}
                                xs={12}
                                key={index}
                                className={classNames({
                                    'border-bottom border-xxl-0 border-xl-0 border-lg-end':
                                        index !== statsConfig.length - 1,
                                    'pb-3 pb-xxl-0 pb-xl-0': index === 0,
                                    'py-3 py-xxl-0 py-xl-0': index > 0 && index < statsConfig.length - 1,
                                    'pt-3 pt-xxl-0 pt-xl-0': index === statsConfig.length - 1
                                })}
                            >
                                <StatsItem stat={stat} />
                            </Col>
                        ))
                    )}
                </Row>
            </Card.Body>
        </Card>
    );
};

DailyStatistics.propTypes = {};

export default DailyStatistics;
